import {
  IWorkOrderPart,
  WorkOrderItemRejectionStatus,
  WorkOrderPriority,
} from "../../models/WorkOrder";
import { PartStatus } from "../../models/Part";
import printHtml from "../../utils/printHtml";

export interface WorkOrderPlanHeader {
  orderNumber?: string | null;
  title?: string | null;
  priority?: WorkOrderPriority | null;
}

const esc = (value: string): string =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const formatCurrency = (amount: number): string =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(amount);

const priorityLabel = (priority: WorkOrderPriority): string => {
  switch (priority) {
    case WorkOrderPriority.Low:
      return "Low";
    case WorkOrderPriority.Medium:
      return "Medium";
    case WorkOrderPriority.High:
      return "High";
    case WorkOrderPriority.Critical:
      return "Critical";
    default:
      return "Unknown";
  }
};

const statusCell = (part: IWorkOrderPart): string => {
  if (part.rejectionStatus === WorkOrderItemRejectionStatus.Scrapped) {
    return '<span style="color:#b91c1c">Rejected · Scrapped</span>';
  }
  if (part.rejectionStatus === WorkOrderItemRejectionStatus.QC) {
    return '<span style="color:#b45309">Rejected · QC</span>';
  }
  if (part.partStatus !== null && part.partStatus !== undefined) {
    return esc(PartStatus[part.partStatus] ?? "Unknown");
  }
  return '<span style="color:#6b7280">—</span>';
};

const vehicleIdentityCell = (part: IWorkOrderPart): string => {
  const lines = [part.stockNumber, part.vrm, part.vin].filter((v): v is string => !!v?.trim());
  if (lines.length === 0) return "—";
  return lines
    .map((line, i) => (i === 0 ? esc(line) : `<div class="donor">${esc(line)}</div>`))
    .join("");
};

const partRow = (part: IWorkOrderPart): string => `
  <tr>
    <td class="tick"></td>
    <td class="part">
      ${esc(part.title || "Part")}
      ${part.donorVehicleDescription
        ? `<div class="donor">${esc(part.donorVehicleDescription)}</div>`
        : ""}
    </td>
    <td>${vehicleIdentityCell(part)}</td>
    <td>${esc(part.accountName || "—")}</td>
    <td>${esc(part.partCategoryName || "—")}</td>
    <td>${esc(part.warehouseLocation || "—")}</td>
    <td class="price">${part.price === null || part.price === undefined ? "—" : formatCurrency(part.price)}</td>
    <td>${statusCell(part)}</td>
  </tr>`;

export default function printWorkOrderParts(
  parts: IWorkOrderPart[],
  header: WorkOrderPlanHeader = {}
) {
  if (parts.length === 0) return;

  const totalValue = parts.reduce((sum, p) => sum + (p.price ?? 0), 0);

  const subtitleParts = [
    header.orderNumber,
    header.title,
    header.priority !== null && header.priority !== undefined
      ? `Priority: ${priorityLabel(header.priority)}`
      : null,
  ]
    .map((v) => (v ?? "").toString().trim())
    .filter(Boolean);

  const html = `
    <html>
      <head>
        <title>Work Order</title>
        <style>
          * {
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          body {
            font-family: Arial, Helvetica, sans-serif;
            color: #111827;
            margin: 24px;
          }
          h1 {
            font-size: 22px;
            font-weight: 700;
            color: #1e293b;
            margin: 0;
          }
          .subtitle {
            font-size: 13px;
            color: #6b7280;
            margin: 2px 0 20px;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            font-size: 12px;
          }
          th, td {
            border: 1px solid #d1d5db;
            padding: 5px 8px;
            text-align: left;
          }
          th {
            background-color: #f3f4f6;
            font-size: 11px;
            letter-spacing: 0.03em;
            color: #374151;
          }
          tr {
            page-break-inside: avoid;
          }
          th.tick, td.tick {
            width: 28px;
            text-align: center;
          }
          td.tick {
            height: 22px;
          }
          td.part {
            font-weight: 600;
          }
          td.part .donor {
            font-weight: 400;
            font-size: 11px;
            color: #6b7280;
            margin-top: 2px;
          }
          th.price, td.price {
            text-align: right;
            white-space: nowrap;
          }
          tfoot td {
            font-weight: 700;
            background-color: #f9fafb;
          }
        </style>
      </head>
      <body>
        <h1>Work Order</h1>
        ${subtitleParts.length > 0 ? `<div class="subtitle">${esc(subtitleParts.join(" · "))}</div>` : ""}
        <table>
          <thead>
            <tr>
              <th class="tick">✓</th>
              <th>PART</th>
              <th>STOCK / REG / VIN</th>
              <th>ACCOUNT</th>
              <th>PART CATEGORY</th>
              <th>LOCATION</th>
              <th class="price">PRICE</th>
              <th>STATUS</th>
            </tr>
          </thead>
          <tbody>${parts.map(partRow).join("")}</tbody>
          <tfoot>
            <tr>
              <td></td>
              <td colspan="5">Total (${parts.length} ${parts.length === 1 ? "item" : "items"})</td>
              <td class="price">${formatCurrency(totalValue)}</td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </body>
    </html>`;

  printHtml(html);
}
